import { useState } from "react";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import NavDropdown from "react-bootstrap/NavDropdown";
import { FaCog } from "react-icons/fa";
import { Form, Modal, Button, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "./navbar.css";
import {
  FUTUREMAP_PARSE_URL,
  NUM_OF_CHARTS,
  NUM_OF_CHARTS_CHANGE_EVENT,
} from "./constants";
import { getHeaders, getStoredNumOfCharts, setStoredNumOfCharts } from "./utils";
import { getSSEStatus, setSSEStatus } from "./sse.js";
import EnergyCaptureModal from "./energy-capture-modal";
import OrangeBarModal from "./orange-bar-modal";
import ChartCaptureModal from "./chart-capture-modal";
import SchedulerModal from "./scheduler-modal";
import MmbDepthModal from "./mmb-depth-modal";

const MMBNavbar = () => {
  const getTomorrow = () => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  };

  const [showSchedulerModal, setShowSchedulerModal] = useState(false);
  const [showEnergyCaptureModal, setShowEnergyCaptureModal] = useState(false);
  const [showOrangeBarModal, setShowOrangeBarModal] = useState(false);
  const [showChartCaptureModal, setShowChartCaptureModal] = useState(false);
  const [showFutureMapModal, setShowFutureMapModal] = useState(false);
  const [showMmbDepthModal, setShowMmbDepthModal] = useState(false);

  // Browser-local preferences. These stay in the dropdown rather than moving
  // into the scheduler modal: both live in localStorage and apply to this
  // browser alone, so there is nothing for the server to report about them.
  const [sseEnabled, setSSEEnabled] = useState(getSSEStatus());
  const [numOfCharts, setNumOfCharts] = useState(getStoredNumOfCharts());

  const [futureMapDate, setFutureMapDate] = useState(getTomorrow());
  const [futureMapText, setFutureMapText] = useState("");
  const [isAddingFutureMap, setIsAddingFutureMap] = useState(false);

  const toggleSSEStatus = () => {
    setSSEStatus(!sseEnabled);
    setSSEEnabled(getSSEStatus());
  };

  // The home layout is a client side preference: it is persisted in
  // localStorage and the live view picks the change up through this event.
  const changeNumOfCharts = (value) => {
    const effective = setStoredNumOfCharts(value);
    setNumOfCharts(effective);
    window.dispatchEvent(
      new CustomEvent(NUM_OF_CHARTS_CHANGE_EVENT, { detail: effective })
    );
  };

  const handleOpenFutureMapModal = () => {
    setFutureMapDate(getTomorrow());
    setShowFutureMapModal(true);
  };

  const handleAddFutureMap = async () => {
    if (!futureMapText || !futureMapText.trim()) {
      alert("Text cannot be empty or contain only spaces.");
      return;
    }

    setIsAddingFutureMap(true);
    try {
      const year = futureMapDate.getFullYear();
      const month = String(futureMapDate.getMonth() + 1).padStart(2, '0');
      const day = String(futureMapDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios({
        method: "post",
        url: FUTUREMAP_PARSE_URL,
        headers: {
          ...getHeaders(),
          "Content-Type": "application/json",
        },
        withCredentials: true,
        data: {
          text: futureMapText,
          date: dateStr
        }
      });

      if (response.status === 200) {
        setShowFutureMapModal(false);
        setFutureMapText("");
        // No need to reset date here as handleOpenFutureMapModal handles it next time
      }
    } catch (error) {
      console.error("Error adding FutureMap:", error);
      const errorDetails = error.response?.data || "No error details provided.";
      const formattedJson = JSON.stringify(errorDetails, null, 2);
      alert("Failed to add FutureMap.\n\nError Details:\n" + formattedJson);
    } finally {
      setIsAddingFutureMap(false);
    }
  };

  const handleCancelFutureMap = () => {
    setShowFutureMapModal(false);
    setFutureMapText("");
  };

  return (
    <Navbar bg="dark" data-bs-theme="dark">
      <Container>
        <Navbar.Brand>FM</Navbar.Brand>
        <Nav className="me-auto">
          <Nav.Link href="/">Home</Nav.Link>
          <Nav.Link href="/history">History</Nav.Link>
        </Nav>
        <NavDropdown
          align="end"
          title={<FaCog className="settings-icon" />}
          id="settings-dropdown"
          onToggle={(isOpen) => {
            if (isOpen) {
              setNumOfCharts(getStoredNumOfCharts());
            }
          }}
        >
          <NavDropdown.Item onClick={() => setShowSchedulerModal(true)}>
            Manage Scheduler
          </NavDropdown.Item>
          <NavDropdown.Divider />
          <div className="setting-item">
            <div className={`status-indicator ${sseEnabled ? "enabled" : "disabled"}`} />
            Server events
            <Form.Check
              type="switch"
              checked={sseEnabled}
              onChange={toggleSSEStatus}
            />
          </div>
          <div className="setting-item">
            Home Layout
            <Form.Select
              size="sm"
              className="layout-select"
              value={numOfCharts}
              onChange={(e) => changeNumOfCharts(e.target.value)}
            >
              <option value={NUM_OF_CHARTS.TWO}>2 grids</option>
              <option value={NUM_OF_CHARTS.FOUR}>4 grids</option>
            </Form.Select>
          </div>
          <NavDropdown.Divider />
          <NavDropdown.Item onClick={handleOpenFutureMapModal}>
            Add FutureMap
          </NavDropdown.Item>
          <NavDropdown.Item onClick={() => setShowMmbDepthModal(true)}>
            Sync MMB Depth
          </NavDropdown.Item>
          <NavDropdown.Item onClick={() => setShowEnergyCaptureModal(true)}>
            Energy Capture Regions…
          </NavDropdown.Item>
          <NavDropdown.Item onClick={() => setShowOrangeBarModal(true)}>
            Debug Orange Bar…
          </NavDropdown.Item>
          <NavDropdown.Item onClick={() => setShowChartCaptureModal(true)}>
            Debug Chart Capture…
          </NavDropdown.Item>
        </NavDropdown>
      </Container>

      <Modal 
        show={showFutureMapModal} 
        onHide={handleCancelFutureMap} 
        size="lg"
        data-bs-theme="dark"
        className="futuremap-modal"
      >
        <Modal.Header closeButton className="bg-dark border-secondary">
          <Modal.Title className="text-light">Add FutureMap</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-light">
          <Form.Group className="mb-3">
            <Form.Label className="text-light">Date</Form.Label>
            <div>
              <DatePicker
                selected={futureMapDate}
                onChange={(date) => setFutureMapDate(date)}
                dateFormat="yyyy-MM-dd"
                className="form-control bg-dark text-light border-secondary"
              />
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="text-light">Text</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              value={futureMapText}
              onChange={(e) => setFutureMapText(e.target.value)}
              style={{ width: "100%", minHeight: "200px" }}
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="bg-dark border-secondary">
          <Button variant="secondary" onClick={handleCancelFutureMap} disabled={isAddingFutureMap}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddFutureMap} disabled={isAddingFutureMap}>
            {isAddingFutureMap ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Adding...
              </>
            ) : (
              "Add"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <SchedulerModal
        show={showSchedulerModal}
        onHide={() => setShowSchedulerModal(false)}
      />

      <MmbDepthModal show={showMmbDepthModal} onHide={() => setShowMmbDepthModal(false)} />

      {/* The region picker can also flip the energy capture enabled flag, which
          is why the scheduler modal re-reads its status every time it opens
          rather than caching it here. */}
      <EnergyCaptureModal
        show={showEnergyCaptureModal}
        onHide={() => setShowEnergyCaptureModal(false)}
      />

      {/* Read-only, unlike the energy modal above: the orange bar alerts have
          nothing to configure from here, so this only ever screenshots. */}
      <OrangeBarModal
        show={showOrangeBarModal}
        onHide={() => setShowOrangeBarModal(false)}
      />

      {/* Runs the nightly capture without its upload, so nothing here can put a
          chart on R2 or overwrite one already filed. */}
      <ChartCaptureModal
        show={showChartCaptureModal}
        onHide={() => setShowChartCaptureModal(false)}
      />
    </Navbar>
  );
};

export default MMBNavbar;
